import { useCallback, useEffect, useRef, useState } from 'react'

import { BeforeFormChangeHandler, useFormData } from 'brief-form'
import { useGetApplicationsWebPage } from 'commons/hooks/bank/applications'
import { UseWebPageProps, WebPage } from 'commons/types/DTO/bank'
import { CircularProgress } from 'ui/components'
import { Dialog } from 'ui/components/DialogV2'
import { Page } from 'ui/components/Page'
import { useTableState } from 'ui/components/Table'

import { Actions } from './components/Actions'
import { ApplicationsListTable } from './components/ApplicationsListTable'
import { ApplicationListFilterValue } from './components/ApplicationsListTable/ApplicationListFilterValue'
import { CreateApplication } from './components/CreateApplication'

const APPLICATIONS_PAGE_NUMBER = 1
const APPLICATIONS_PER_PAGE = 10

const defaultFilterFormValue = {
  status: null,
  createdById: null,
  currentOwnerId: null,
  searchQuery: ''
}

const ApplicationList = () => {
  const [webPageData, setWebPageData] = useState<WebPage | undefined>(undefined)
  const isFirstRequest = useRef(true)
  const [webPageFilter, setWebPageFilter] = useState<UseWebPageProps>({
    status: null,
    sortAsc: false,
    page: APPLICATIONS_PAGE_NUMBER,
    size: APPLICATIONS_PER_PAGE,
    searchQuery: '',
    createdById: null,
    currentOwnerId: null,
    sortField: ''
  })

  const tableState = useTableState<Partial<UseWebPageProps>>({
    size: APPLICATIONS_PER_PAGE,
    page: APPLICATIONS_PAGE_NUMBER,
    order: [{ field: 'lastModified', direction: 'DESC' }],
    filter: {
      status: null,
      createdById: null,
      currentOwnerId: null,
      searchQuery: ''
    }
  })

  const onBeforeChange: BeforeFormChangeHandler<ApplicationListFilterValue> = useCallback(
    ({ value, errors }) => {
      setWebPageFilter((filter) => ({
        ...filter,
        status: value.status,
        page: APPLICATIONS_PAGE_NUMBER,
        size: APPLICATIONS_PER_PAGE,
        searchQuery: value.searchQuery,
        createdById: value.createdById,
        currentOwnerId: value.currentOwnerId
      }))
      tableState.onPageChange(APPLICATIONS_PAGE_NUMBER, tableState.size)

      return {
        value,
        errors
      }
    },
    [tableState]
  )

  const filterForm = useFormData<ApplicationListFilterValue>({
    onBeforeChange,
    initialValue: defaultFilterFormValue
  })

  const { data: webPage, isFetching: isFetchingWebPage } = useGetApplicationsWebPage(webPageFilter)
  const [showCreateModal, setShowCreateModal] = useState(false)

  const clearHandler = useCallback(() => {
    isFirstRequest.current = true

    setWebPageFilter((filter) => ({
      ...filter,
      page: APPLICATIONS_PAGE_NUMBER,
      searchQuery: '',
      status: null,
      createdById: null,
      currentOwnerId: null
    }))
    filterForm.set({
      value: defaultFilterFormValue,
      reset: true
    })
    tableState.onPageChange(APPLICATIONS_PAGE_NUMBER, tableState.size)
    tableState.filterForm.set({
      value: {
        ...tableState.filterForm.config.value,
        searchQuery: '',
        createdById: null,
        currentOwnerId: null,
        status: null
      },
      reset: true
    })
  }, [filterForm, tableState])

  const onSetWebPagePagination = (page: number, size: number) => {
    tableState.onPageChange(page, size)
    setWebPageFilter((filter) => ({ ...filter, page, size }))
  }

  const onSetWebPageSorting = (direction: 'DESC' | 'ASC') => {
    tableState.onOrderChange({ field: 'lastModified', direction: direction })
    setWebPageFilter((filter) => ({ ...filter, sortAsc: direction === 'ASC' }))
  }

  const onSetWebPageQuery = (searchWord: string) => {
    filterForm.config.onChange({ ...filterForm.config.value, searchQuery: searchWord }, {})
    tableState.onPageChange(APPLICATIONS_PAGE_NUMBER, tableState.size)
    tableState.filterForm.set({
      value: {
        ...tableState.filterForm.config.value,
        searchQuery: searchWord
      }
    })
  }

  useEffect(() => {
    if (webPage) {
      setWebPageData(webPage)
      filterForm.set({
        value: {
          ...filterForm.config.value,
          status: webPage.form.status || null,
          createdById: webPage.form.createdById || null,
          currentOwnerId: webPage.form.currentOwnerId || null
        },
        reset: isFirstRequest.current
      })

      isFirstRequest.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webPage])

  if (!webPageData) {
    return <CircularProgress centered />
  }

  const { showEnableChooseExistingRelationship, enableCreateApplication } = webPageData

  return (
    <>
      <Dialog
        id="create-application"
        isOpened={showCreateModal}
        content={<CreateApplication showExistingRelationshipToggle={showEnableChooseExistingRelationship} />}
        onClose={() => setShowCreateModal(false)}
      />
      <Page
        title="Application List"
        actions={<Actions showNewButton={enableCreateApplication} onOpenCreateModal={() => setShowCreateModal(true)} />}
      >
        <ApplicationsListTable
          webPageData={webPageData}
          onClean={clearHandler}
          loading={isFetchingWebPage}
          tableState={tableState}
          filterForm={filterForm}
          onSetWebPagePagination={onSetWebPagePagination}
          onSetWebPageSorting={onSetWebPageSorting}
          onSetWebPageQuery={onSetWebPageQuery}
        />
      </Page>
    </>
  )
}

export default ApplicationList
